import React, { useState, useEffect, useCallback } from "react";
import { Header } from "build-ts/metadata";
import { JobRunStatus, ScheduledJobRunProto } from "build-ts/schedule";
import { getScheduledJobRun } from "services/ScheduleServices";
import showToast from "hooks/useCustomToast";
import { useUser } from "context/UserContext";
import { epochConverterWithTimeZone } from "utils/dateUtils";
import AnalysisRunCard from "../components/AnalysisRunCard";
import { getStatusInfo } from "utils/nameUtils";

export interface StatusInfo {
  color: string;
  label: string;
}

interface AnalysisRunCardContainerProps {
  jobRun: Header;
  hideRunDetails?: boolean;
}

const AnalysisRunCardContainer: React.FC<AnalysisRunCardContainerProps> = ({ jobRun, hideRunDetails = false }) => {
  const [runDetails, setRunDetails] = useState<ScheduledJobRunProto | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { defaultTimeZone } = useUser();

  useEffect(() => {
    const fetchRunDetails = async () => {
      setIsLoading(true);
      const details = await getScheduledJobRun(jobRun?.id || "");
      setRunDetails(details ?? null);
      setIsLoading(false);
    };
    fetchRunDetails();
  }, [jobRun?.id]);

  const handleRefreshRun = useCallback(async () => {
    if (!jobRun.id) return;
    setIsRefreshing(true);
    const details = await getScheduledJobRun(jobRun.id);
    if (details) {
      setRunDetails(details);
      showToast("Run status refreshed", "", "success");
    } else {
      showToast("Failed to refresh run status", "", "error");
    }
    setIsRefreshing(false);
  }, [jobRun.id]);

  const handleHeaderClick = useCallback(() => {
    const sessionId = runDetails?.scenario_run?.analyst_chat_session_id;
    if (sessionId) {
      window.open(`/chat/${sessionId}`, "_blank");
    }
  }, [runDetails?.scenario_run?.analyst_chat_session_id]);

  const formattedDate = jobRun?.created_date_ms
    ? epochConverterWithTimeZone(jobRun.created_date_ms, "d-MMM-yyyy, HH:mm", defaultTimeZone) +
      " " +
      defaultTimeZone.toUpperCase()
    : "N/A";

  const statusInfo = runDetails ? getStatusInfo(runDetails.status ?? JobRunStatus.PENDING) : null;

  return (
    <AnalysisRunCard
      runDetails={runDetails}
      isLoading={isLoading}
      isRefreshing={isRefreshing}
      formattedDate={formattedDate}
      statusInfo={statusInfo}
      onRefresh={handleRefreshRun}
      onHeaderClick={handleHeaderClick}
      hideRunDetails={hideRunDetails}
    />
  );
};

export default AnalysisRunCardContainer;
